import classNames from 'classnames';
import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MenuCategory,
  useMenuCategoryGroups,
} from '../../../hooks/useMenuCategoryGroups';
import { getStringLength } from '../../../utils/getStringLengs';
import ChevronDown from '../../../icons/ChevronDown.svg';

import s from './index.scss';
import { useTranslation } from '@wix/wix-i18n-config';

interface IMegaMenuV3Props {
  onCategoryClick: (slug: string) => void;
}

export const MegaMenuV3: FC<IMegaMenuV3Props> = ({ onCategoryClick }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuItemSize, setMenuItemSize] = useState<number>(0);
  const menuCategoryGroups = useMenuCategoryGroups();
  const [openCategoryIndex, setOpenCategoryIndex] = useState<number | null>(
    null,
  );
  const { t } = useTranslation();

  const handleTitleClick = (index: number) => {
    setOpenCategoryIndex(openCategoryIndex === index ? null : index);
  };

  const handleMouseEnter = (index: number) => {
    setOpenCategoryIndex(index);
  };

  const handleMouseLeave = () => {
    setOpenCategoryIndex(null);
  };

  const handleLinkClick = (slug: string) => {
    onCategoryClick(slug);
    setOpenCategoryIndex(null);
  };

  const handleResize = useCallback(() => {
    if (menuRef.current) {
      const width = Math.round(
        menuRef.current.getBoundingClientRect().width - 181,
      );
      setMenuItemSize(width / menuCategoryGroups.length);
    }
  }, [menuCategoryGroups.length]);

  useEffect(() => {
    handleResize();
    globalThis?.window?.addEventListener?.('resize', handleResize);
    return () => {
      globalThis?.window?.removeEventListener?.('resize', handleResize);
    };
  }, [handleResize]);

  // todo: move to scss
  const getDropdownPosition = (
    categories: MenuCategory[],
    isLastCategory: boolean,
  ) => {
    const columns = Math.ceil(categories.length / 5);

    if (!menuItemSize) {
      return { left: '-1px', right: '-1px' };
    }

    const longestItem = Math.ceil(
      Math.max(
        ...categories.map(({ label }) =>
          getStringLength({
            str: label,
            fontSize: 14,
            fontWeight: 500,
          }),
        ),
      ),
    );

    const calculatedWidth = 2 * 24 + (columns - 1) * 60 + columns * longestItem;

    if (columns === 1 && calculatedWidth <= menuItemSize) {
      return { left: '-1px', right: '-1px' };
    }

    // If the title is too long, we need to adjust the width
    if (columns === 1 && calculatedWidth > menuItemSize) {
      const shift = `-${menuItemSize + 1}px`;
      return isLastCategory
        ? { left: shift, right: '-1px' }
        : { left: '-1px', right: shift };
    }

    // -1 - shift on one column
    // +1 - border width
    const shift = `-${menuItemSize * (columns - 1) + 1}px`;

    // Last category with multiple columns: extend left instead of right
    if (isLastCategory && columns > 1) {
      return { left: shift, right: '-1px' };
    }

    return { left: '-1px', right: shift };
  };

  return (
    <div className={s.container} ref={menuRef}>
      <div className={classNames(s.browseBy)}>
        {t('app.topBar.v2.menu.toggle.label')}
      </div>
      <div className={s.menuItems}>
        {menuCategoryGroups.map(({ title, categories }, index) => {
          const isLastCategory = index === menuCategoryGroups.length - 1;
          const position = getDropdownPosition(categories, isLastCategory);

          return (
            <div
              key={index}
              className={classNames(s.category, {
                [s.open]: openCategoryIndex === index,
              })}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={classNames(s.titleBtn, {
                  [s.active]:
                    openCategoryIndex === index ||
                    categories.find((c) => c.isActive),
                })}
                onClick={() => handleTitleClick(index)}
              >
                <span className={s.title}>{title}</span>
                <ChevronDown className={s.btnIcon} />
              </button>
              <div
                className={classNames(s.listContainer, {
                  [s.listContainerFourRows]: categories.length <= 4,
                })}
                style={
                  {
                    '--left': position.left,
                    '--right': position.right,
                  } as React.CSSProperties
                }
              >
                <ul
                  className={classNames(s.list, {
                    [s.fourRows]: categories.length <= 4,
                  })}
                >
                  {categories.map(({ label, url, slug, isActive }) => (
                    <li className={s.item} key={slug} data-content={label}>
                      {slugToIcon[slug] && (
                        <div
                          className={classNames(s.circleIcon, slugToIcon[slug])}
                        ></div>
                      )}
                      <Link
                        to={url}
                        onClick={() => handleLinkClick(slug)}
                        className={classNames(s.link, { [s.active]: isActive })}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const slugToIcon: Record<string, string> = {
  dark: s.circleDark,
  bright: s.circleWhite,
  gradient: s.circleColorful,
  neon: s.circleYellow,
  'b-w': s.circleBlackWhite,

  grey: s.circleGrey,
  blue: s.circleBlue,
  red: s.circleRed,
  green: s.circleGreen,
  pink: s.circlePink,
  purple: s.circlePurple,
  orange: s.circleOrange,
  brown: s.circleBrown,
};
